
import { useState, useRef, useEffect } from "react";
import { Upload, Sparkles, Rocket, Play, RefreshCw, Kanban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ssrDocument } from "@/utils/ssrUtils";

const PricingSection = () => {
  const [hoveredFeature, setHoveredFeature] = useState<"import" | "optimize" | "launch" | "startCoding" | "keepContext">("import");
  const videoRef = useRef<HTMLVideoElement>(null);
  
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
  }, [hoveredFeature]);

  const scrollToWaitlist = () => {
    const waitlistSection = ssrDocument.getElementById('waitlist-section');
    if (waitlistSection) {
      waitlistSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const videoUrls = {
    import: "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/crd-062425_import_md.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9jOTljZjI2OC04OTEzLTRhYjktYmI4YS0yMTE1MjQ2YzRjNjQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NyZC0wNjI0MjVfaW1wb3J0X21kLm1wNCIsImlhdCI6MTc1MDg0MTc0NSwiZXhwIjoxNzgyMzc3NzQ1fQ.2a9Mz8SXGigdsH14Gbv1XETTIPxxA3GEFCOpAT8950g",
    optimize: "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/crd-062425_optimize_tasks.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9jOTljZjI2OC04OTEzLTRhYjktYmI4YS0yMTE1MjQ2YzRjNjQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NyZC0wNjI0MjVfb3B0aW1pemVfdGFza3MubXA0IiwiaWF0IjoxNzUwODQxODc1LCJleHAiOjE3ODIzNzc4NzV9.K07F7OCQmQy33t-M0DbUmZ8mMdcP4uAlOdhdD_QoSx4",
    launch: "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/crd-062425_start_project.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9jOTljZjI2OC04OTEzLTRhYjktYmI4YS0yMTE1MjQ2YzRjNjQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NyZC0wNjI0MjVfc3RhcnRfcHJvamVjdC5tcDQiLCJpYXQiOjE3NTA4NDE5NTQsImV4cCI6MTc4MjM3Nzk1NH0.tEwJ2-qfHtlx9UGGPzqvkPv-_OMp4i99qDsMRw-53As",
    startCoding: "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/crd-062425_mcp_start_project.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9jOTljZjI2OC04OTEzLTRhYjktYmI4YS0yMTE1MjQ2YzRjNjQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NyZC0wNjI0MjVfbWNwX3N0YXJ0X3Byb2plY3QubXA0IiwiaWF0IjoxNzUwODQxOTcxLCJleHAiOjE3ODIzNzc5NzF9.SPS-DwmsWB2eczkH73ND9wO3WWbk4Rhs6ScbRfC8HeM",
    keepContext: "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/crd-062425_mcp_get_prompt.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9jOTljZjI2OC04OTEzLTRhYjktYmI4YS0yMTE1MjQ2YzRjNjQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NyZC0wNjI0MjVfbWNwX2dldF9wcm9tcHQubXA0IiwiaWF0IjoxNzUwODQyMDA4LCJleHAiOjE3ODIzNzgwMDh9.kZ98r1NGvUmZOX2aPkyFrhAG0lvKizmBSFhxdi350A0"
  };

  return (
    <section 
      id="how-it-works" 
      className="section-padding bg-muted relative scroll-section" 
      style={{
        backgroundImage: `url('https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/cr_bg_2.webp?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NyX2JnXzIud2VicCIsImlhdCI6MTc0NDk2MTQwNywiZXhwIjoxNzc2NDk3NDA3fQ.IfeUNJDzKofonFVKJ4W7xXhMNEFh392WSjUZ98qCVXs')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="absolute inset-0 bg-coderide-black/50"></div>
      <div className="container relative z-10">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 text-white drop-shadow-lg">
          Five Simple Steps. No Complexity.
        </h2>
        <p className="text-center text-white/90 mb-12 max-w-3xl mx-auto drop-shadow">
          Let CodeRide take care of your tasks and enjoy watching your agent <span className="bg-[#7867EE] text-white px-2 py-1 rounded font-mono">autonomously completing</span> your <strong className="font-semibold text-white">project</strong>.
        </p>
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch max-w-7xl mx-auto">
          <div className="lg:col-span-4 flex flex-col justify-between space-y-4">
            <div 
              className="space-y-2 p-3 rounded-lg transition-all duration-300 hover:bg-[#F5F5F5]/40 hover:shadow-xl hover:scale-[1.02] backdrop-blur-sm cursor-pointer border border-white/10 bg-[#F5F5F5]/5" 
              onMouseEnter={() => setHoveredFeature("import")}
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                  <Upload className="h-5 w-5 text-white" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-lg font-bold text-white">Import Documentation</h3>
                  <p className="text-white/80 leading-relaxed text-sm">
                    Upload your project documentation or task lists. CodeRide analyzes and automatically extracts actionable development tasks.
                  </p>
                </div>
              </div>
            </div>
            
            <div 
              className="space-y-2 p-3 rounded-lg transition-all duration-300 hover:bg-[#F5F5F5]/40 hover:shadow-xl hover:scale-[1.02] backdrop-blur-sm cursor-pointer border border-white/10 bg-[#F5F5F5]/5" 
              onMouseEnter={() => setHoveredFeature("optimize")}
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-lg font-bold text-white">Optimize Tasks</h3>
                  <p className="text-white/80 leading-relaxed text-sm">
                    CodeRide enhances each task with AI-optimized context, instructions, and project-specific details for your agent.
                  </p>
                </div>
              </div>
            </div>
            
            <div 
              className="space-y-2 p-3 rounded-lg transition-all duration-300 hover:bg-[#F5F5F5]/40 hover:shadow-xl hover:scale-[1.02] backdrop-blur-sm cursor-pointer border border-white/10 bg-[#F5F5F5]/5" 
              onMouseEnter={() => setHoveredFeature("launch")}
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                  <Play className="h-5 w-5 text-white" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-lg font-bold text-white">Launch Project</h3>
                  <p className="text-white/80 leading-relaxed text-sm">
                    Initialize your project setup and prepare the development environment for AI agent execution.
                  </p>
                </div>
              </div>
            </div>
            
            <div 
              className="space-y-2 p-3 rounded-lg transition-all duration-300 hover:bg-[#F5F5F5]/40 hover:shadow-xl hover:scale-[1.02] backdrop-blur-sm cursor-pointer border border-white/10 bg-[#F5F5F5]/5" 
              onMouseEnter={() => setHoveredFeature("startCoding")}
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                  <Rocket className="h-5 w-5 text-white" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-lg font-bold text-white">Start Coding</h3>
                  <p className="text-white/80 leading-relaxed text-sm">
                    Your AI agent connects via MCP and begins autonomous development in your preferred IDE with complete project understanding.
                  </p>
                </div>
              </div>
            </div>
            
            <div 
              className="space-y-2 p-3 rounded-lg transition-all duration-300 hover:bg-[#F5F5F5]/40 hover:shadow-xl hover:scale-[1.02] backdrop-blur-sm cursor-pointer border border-white/10 bg-[#F5F5F5]/5" 
              onMouseEnter={() => setHoveredFeature("keepContext")}
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                  <RefreshCw className="h-5 w-5 text-white" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-lg font-bold text-white">Keep Context Flowing</h3>
                  <p className="text-white/80 leading-relaxed text-sm">
                    Your agent continuously retrieves and completes tasks while preserving all architectural decisions and coding patterns.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-8 rounded-lg overflow-hidden shadow-2xl relative bg-[#F5F5F5]/5 backdrop-blur-sm border border-white/10 flex items-center">
            <video 
              ref={videoRef}
              key={hoveredFeature}
              src={videoUrls[hoveredFeature]}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        
        <div className="text-center mt-12 relative z-10">
          <Button 
            variant="primary"
            size="lg"
            showArrow={true}
            onClick={() => window.open('https://app.coderide.ai/auth/sign-up', '_blank')}
          >
            Get Started Now
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
